import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import { getAllProducts, getProductsByCategoryId, getProductCategories } from '../api/productService';
import ProductCard from '../components/products/ProductCard';
import FilterDropdown from '../components/products/FilterDropdown';
import Seo from '../components/common/Seo';

// Custom hook for debounced search
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

// Loading skeleton component (keep exact same design)
const ProductSkeleton = () => (
    <div className="animate-pulse">
        <div className="bg-gray-200 rounded-lg h-[200px] w-full mb-2"></div>
        <div className="bg-gray-200 h-4 w-3/4 mb-2"></div>
        <div className="bg-gray-200 h-4 w-1/2"></div>
    </div>
);

const ProductCollection = () => {
    const { category } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);

    // State variables - server-side filtering approach
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState(queryParams.get('search') || '');
    const [currentPage, setCurrentPage] = useState(parseInt(queryParams.get('page')) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [sortOption, setSortOption] = useState(queryParams.get('sort') || 'newest');
    const [filterType, setFilterType] = useState(queryParams.get('type') || '');
    const [categoryInfo, setCategoryInfo] = useState(null);
    const [categoryId, setCategoryId] = useState(null);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    const perPage = 15; // 5 columns x 3 rows = 15 products per page

    // Debounce search term to prevent excessive API calls
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Callbacks for filter dropdowns (defined at component level, not inside JSX)
    const handleSortChange = useCallback((value) => {
        setSortOption(value);
        setCurrentPage(1);
    }, []);

    const handleFilterChange = useCallback((value) => {
        if (category && value) {
            // On a category page, navigate to the selected category's page
            const categorySlug = value.toLowerCase().replace(/\s+/g, '-');
            navigate(`/products/${categorySlug}`);
        } else if (category && !value) {
            // "All Categories" selected on a category page → go to all products
            navigate('/products');
        } else {
            setFilterType(value);
            setCurrentPage(1);
        }
    }, [category, navigate]);

    // Convert category slug to readable format or use fetched category info
    // This should remain static and not change based on filtering
    const categoryDisplayName = categoryInfo 
        ? (categoryInfo.category_name || categoryInfo.name || categoryInfo.title)
        : category 
        ? category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        : '';

    // Static display name for breadcrumb and title (never changes based on filter)
    const staticCategoryDisplayName = categoryDisplayName;

    // Fetch all categories (for both filter dropdown and category info) in a single API call
    const fetchCategories = useCallback(async () => {
        try {
            setCategoriesLoading(true);
            const categoriesData = await getProductCategories({
                page: 1,
                perPage: 100,
                search: '',
                sorting: 'asc'
            });

            const fetchedCategories = categoriesData.categories || categoriesData.data || categoriesData;
            const categoriesArray = Array.isArray(fetchedCategories) ? fetchedCategories : [];
            setCategories(categoriesArray);

            // Also resolve category info from the same data if we have a category slug
            if (category && categoriesArray.length > 0) {
                const foundCategory = categoriesArray.find(cat => {
                    const categoryName = cat.category_name || cat.name || cat.title || '';
                    const categorySlug = categoryName.toLowerCase().replace(/\s+/g, '-');
                    return categorySlug === category || categoryName.toLowerCase().includes(category.toLowerCase());
                });

                if (foundCategory) {
                    setCategoryInfo(foundCategory);
                    setCategoryId(foundCategory._id || foundCategory.id || foundCategory.category_id);
                } else {
                    setCategoryInfo(null);
                    setCategoryId(null);
                }
            } else if (!category) {
                setCategoryInfo(null);
                setCategoryId(null);
            }
        } catch (error) {
            setCategories([]);
            setCategoryInfo(null);
            setCategoryId(null);
        } finally {
            setCategoriesLoading(false);
        }
    }, [category]);

    // Fetch products from API with server-side filtering
    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            let data;
            
            // If we have a category ID, use the new API endpoint
            if (categoryId) {
                
                // Category API doesn't support pagination, so we fetch all products
                const categoryParams = {
                    page: currentPage,
                    perPage,
                    sorting: 'asc'
                };

                // Add search parameter (use debounced search term)
                if (debouncedSearchTerm.trim()) {
                    categoryParams.search = debouncedSearchTerm.trim();
                }

                // Add filter type parameter (category name for filtering)
                if (filterType) {
                    // For category API, we'll do client-side filtering by category name
                    // since the API doesn't support category filtering within categories
                }

                // Add sorting parameters
                if (sortOption === 'newest') {
                    categoryParams.sorting = 'desc';
                    categoryParams.sort = 'createdAt';
                } else if (sortOption === 'oldest') {
                    categoryParams.sorting = 'asc';
                    categoryParams.sort = 'createdAt';
                } else if (sortOption === 'name_asc') {
                    categoryParams.sorting = 'asc';
                    categoryParams.sort = 'product_name';
                } else if (sortOption === 'name_desc') {
                    categoryParams.sorting = 'desc';
                    categoryParams.sort = 'product_name';
                }

                data = await getProductsByCategoryId(categoryId, categoryParams);
            } else if (category) {
                // Fallback: If we have category but no ID, use the old method
                
                // Fetch all products for category filtering
                const allProductsParams = {
                    page: 1,
                    perPage: 1000, // Fetch a large number to get all products
                    sorting: 'asc'
                };

                // Add search parameter (use debounced search term)
                if (debouncedSearchTerm.trim()) {
                    allProductsParams.search = debouncedSearchTerm.trim();
                }

                // Add filter type parameter (category name for filtering)
                if (filterType) {
                    // We'll do client-side filtering by category name
                }

                // Add sorting parameters
                if (sortOption === 'newest') {
                    allProductsParams.sorting = 'desc';
                    allProductsParams.sort = 'createdAt';
                } else if (sortOption === 'oldest') {
                    allProductsParams.sorting = 'asc';
                    allProductsParams.sort = 'createdAt';
                } else if (sortOption === 'name_asc') {
                    allProductsParams.sorting = 'asc';
                    allProductsParams.sort = 'product_name';
                } else if (sortOption === 'name_desc') {
                    allProductsParams.sorting = 'desc';
                    allProductsParams.sort = 'product_name';
                }

                data = await getAllProducts(allProductsParams);
            } else {
                // For non-category pages, use normal pagination
                const params = {
                    page: currentPage,
                    perPage,
                    sorting: 'asc'
                };

                // Add search parameter (use debounced search term)
                if (debouncedSearchTerm.trim()) {
                    params.search = debouncedSearchTerm.trim();
                }

                // Add filter type parameter - pass category to API for server-side filtering
                if (filterType) {
                    params.category = filterType;
                }

                // Add sorting parameters
                if (sortOption === 'newest') {
                    params.sorting = 'desc';
                    params.sort = 'createdAt';
                } else if (sortOption === 'oldest') {
                    params.sorting = 'asc';
                    params.sort = 'createdAt';
                } else if (sortOption === 'name_asc') {
                    params.sorting = 'asc';
                    params.sort = 'product_name';
                } else if (sortOption === 'name_desc') {
                    params.sorting = 'desc';
                    params.sort = 'product_name';
                }

                data = await getAllProducts(params);
            }
            
            // Handle different possible response structures
            let productsArray = [];
            let parsedTotalItems = 0;

            if (Array.isArray(data)) {
                // If response is directly an array
                productsArray = data;
                parsedTotalItems = data.length;
            } else if (data && data.products && Array.isArray(data.products)) {
                // If response has products property
                productsArray = data.products;
                parsedTotalItems = data.total || data.totalItems || data.products.length;
            } else if (data && data.data && Array.isArray(data.data)) {
                // Check if this is category API response (each item has product property)
                if (data.data.length > 0 && data.data[0].product) {
                    // Category API response: extract products from each item
                    productsArray = data.data.map(item => ({
                        ...item.product,
                        category: item.category_name,
                        category_name: item.category_name
                    }));
                    parsedTotalItems = productsArray.length;
                } else {
                    // Regular data array response
                    productsArray = data.data;
                    parsedTotalItems = data.total || data.totalItems || data.data.length;
                }
            } else if (data && data.data && data.data.product && Array.isArray(data.data.product)) {
                // If response has nested data.product structure (category API response)
                productsArray = data.data.product;
                parsedTotalItems = data.data.product.length;
            } else {
                productsArray = [];
                parsedTotalItems = 0;
            }
                        
            // Handle category filtering
            let filteredProducts = productsArray;
            let isClientSideFiltered = false;
            
            if (categoryId) {
                const {
                    products: categoryProducts = [],
                    totalItems: categoryTotalItems,
                    totalPages: categoryTotalPages,
                    perPage: categoryPerPage,
                    currentPage: categoryCurrentPage
                } = data || {};

                const normalizedPerPage = Number(categoryPerPage) && Number(categoryPerPage) > 0
                    ? Number(categoryPerPage)
                    : perPage;

                const normalizedTotalItems = Number(categoryTotalItems);
                const resolvedTotalItems = Number.isFinite(normalizedTotalItems)
                    ? normalizedTotalItems
                    : categoryProducts.length;

                const normalizedTotalPages = Number(categoryTotalPages);
                const resolvedTotalPages = Number.isFinite(normalizedTotalPages) && normalizedTotalPages > 0
                    ? normalizedTotalPages
                    : Math.max(1, Math.ceil(resolvedTotalItems / normalizedPerPage));

                const normalizedCurrentPage = Number(categoryCurrentPage);
                const resolvedCurrentPage = Number.isFinite(normalizedCurrentPage) && normalizedCurrentPage > 0
                    ? normalizedCurrentPage
                    : currentPage;

                let categoryFilteredProducts = categoryProducts;
                let appliedClientFilter = false;

                if (filterType) {
                    categoryFilteredProducts = categoryProducts.filter(product => {
                        const productCategory = product?.category || product?.category_name || '';
                        return productCategory.toLowerCase().includes(filterType.toLowerCase());
                    });
                    appliedClientFilter = true;
                }

                if (appliedClientFilter) {
                    const startIndex = (currentPage - 1) * normalizedPerPage;
                    const endIndex = startIndex + normalizedPerPage;
                    const paginatedFilteredProducts = categoryFilteredProducts.slice(startIndex, endIndex);

                    setProducts(paginatedFilteredProducts);
                    setTotalItems(categoryFilteredProducts.length);
                    setTotalPages(Math.max(1, Math.ceil(categoryFilteredProducts.length / normalizedPerPage)));
                } else {
                    setProducts(categoryFilteredProducts);
                    setTotalItems(resolvedTotalItems);
                    setTotalPages(Math.max(1, resolvedTotalPages));

                    if (resolvedCurrentPage !== currentPage) {
                        setCurrentPage(resolvedCurrentPage);
                    }
                }

                return;
            } else if (category) {
                // Fallback: do client-side filtering for category pages when using old method
                isClientSideFiltered = true;
                
                const expectedCategory = category.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ');
                
                filteredProducts = productsArray.filter(product => {
                    // Check multiple possible category fields
                    const productCategory = product.category || product.category_name || '';
                    const productName = product.product_name || product.title || '';
                                    
                    // Check if the product's category matches the expected category
                    const categoryMatch = productCategory.toLowerCase().includes(expectedCategory.toLowerCase()) ||
                                        productCategory.toLowerCase().includes(category.toLowerCase()) ||
                                        // Also check if category name appears in product name as fallback
                                        productName.toLowerCase().includes(expectedCategory.toLowerCase()) ||
                                        productName.toLowerCase().includes(category.toLowerCase());
                    
                    return categoryMatch;
                });
                
            }
            
            // Handle pagination based on whether we used category API or not
            const normalizePositiveNumber = (value, fallback = 0) => {
                const numeric = Number(value);
                return Number.isFinite(numeric) && numeric >= 0 ? numeric : fallback;
            };

            const responseTotalItems =
                data?.totalItems ??
                parsedTotalItems ??
                filteredProducts.length;
            const rawTotalPagesValue =
                data?.totalPages ??
                data?.total_pages ??
                data?.pages ??
                data?.meta?.totalPages ??
                data?.pagination?.totalPages ??
                (responseTotalItems && perPage ? Math.ceil(responseTotalItems / perPage) : 1);
            const responseTotalPagesRaw =
                Number(rawTotalPagesValue) || Math.ceil(responseTotalItems / perPage) || 1;
            const responseTotalPages = normalizePositiveNumber(responseTotalPagesRaw, 1) || 1;

            if (isClientSideFiltered) {
                // Client-side pagination for category pages (fallback method)
                const startIndex = (currentPage - 1) * perPage;
                const endIndex = startIndex + perPage;
                const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
                
                setProducts(paginatedProducts);
                setTotalItems(filteredProducts.length);
                setTotalPages(normalizePositiveNumber(Math.ceil(filteredProducts.length / perPage), 1) || 1);
                
            } else {
                // Server-side pagination for non-category pages (filtering handled by API)
                setProducts(filteredProducts);
                setTotalItems(normalizePositiveNumber(responseTotalItems, filteredProducts.length));
                setTotalPages(responseTotalPages);
                
            }

        } catch (err) {
            setError('Failed to load products. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [categoryId, category, currentPage, debouncedSearchTerm, sortOption, filterType, perPage]);

    // Fetch categories (and category info) when component mounts or category changes
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Fetch products when component mounts or when filters change
    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Update URL when filters change (debounced to prevent excessive navigation)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newParams = new URLSearchParams();
            if (debouncedSearchTerm) newParams.set('search', debouncedSearchTerm);
            if (filterType) newParams.set('type', filterType);
            if (sortOption !== 'newest') newParams.set('sort', sortOption);
            if (currentPage > 1) newParams.set('page', currentPage);
            
            const newSearch = newParams.toString();
            const newPath = category 
                ? `/products/${category}${newSearch ? `?${newSearch}` : ''}`
                : `/products${newSearch ? `?${newSearch}` : ''}`;
            
            navigate(newPath, { replace: true });
        }, 100); // Small delay to batch URL updates

        return () => clearTimeout(timeoutId);
    }, [debouncedSearchTerm, filterType, sortOption, currentPage, category, navigate]);

    // Handle search input changes
    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to page 1 when search changes
    }, []);

    // Reset to page 1 and clear filter when category changes
    useEffect(() => {
        setCurrentPage(1);
        setFilterType('');
    }, [category]);

    // Handle search form submission
    const handleSearchSubmit = useCallback((e) => {
        e.preventDefault();
        // Search will trigger automatically due to useEffect dependency on debouncedSearchTerm
    }, []);

    // Handle pagination
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0);
    }, []);

    // Generate pagination buttons (keep exact same design) - memoized for performance
    const renderPagination = useMemo(() => {
        const pages = [];
        const maxVisiblePages = 3;
        
        const effectiveTotalPages = Math.max(1, totalPages);

        let startPage, endPage;
        
        if (effectiveTotalPages <= maxVisiblePages + 2) {
            startPage = 1;
            endPage = effectiveTotalPages;
        } else {
            if (currentPage <= Math.ceil(maxVisiblePages / 2) + 1) {
                startPage = 1;
                endPage = maxVisiblePages;
            } else if (currentPage >= effectiveTotalPages - Math.floor(maxVisiblePages / 2)) {
                startPage = effectiveTotalPages - maxVisiblePages + 1;
                endPage = effectiveTotalPages;
            } else {
                startPage = currentPage - Math.floor(maxVisiblePages / 2);
                endPage = currentPage + Math.floor(maxVisiblePages / 2);
            }
        }

        // Previous button
        pages.push(
            <button
                key="prev"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-gray-700 disabled:opacity-50 font-inter hover:bg-gray-50" 
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        );

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`flex items-center justify-center w-10 h-10 text-sm font-medium ${currentPage === i
                        ? 'bg-[#EDEDED] text-[#1C1C1C] font-medium rounded-full'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >  {i}
                </button>
            );
        }

        // Next button
        pages.push(
            <button
                key="next"
                onClick={() => handlePageChange(Math.min(effectiveTotalPages, currentPage + 1))}
                disabled={currentPage === effectiveTotalPages}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white text-gray-700 disabled:opacity-50 font-inter hover:bg-gray-50"

            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        );

        return pages;
    }, [currentPage, totalPages, handlePageChange]);

    // Breadcrumb navigation (keep exact same design)
    const renderBreadcrumbs = () => {
        return (
            <nav className="flex items-center text-sm text-gray-600 mb-6 opacity-60" aria-label="Breadcrumb">
                <Link 
                    to="/" 
                    className="transition-colors duration-200 font-medium"
                >
                    Home
                </Link>
                <span className="mx-2 text-gray-400">/</span>
                <Link
                    to="/products"
                    className="transition-colors duration-200 font-medium"
                >
                    Categories
                </Link>
                {category && (
                    <>
                        <span className="mx-2 text-gray-400">/</span>
                        <span className="text-gray-600 font-semibold">{staticCategoryDisplayName}</span>
                    </>
                )}
            </nav>
        );
    };

    return (
        <div className="container-custom">
            <Seo
                title={category ? `${staticCategoryDisplayName} | Agastya Agro Limited` : 'All Products | Agastya Agro Limited'}
                description={
                    category
                        ? `Browse Agastya Agro's ${staticCategoryDisplayName} range and compare trusted solutions for your crop needs.`
                        : "Explore Agastya Agro's full range of crop protection products and quickly find the right solution for your crop and field conditions."
                }
            />
            {/* Search and Filters - keep exact same design */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center pt-[12px] md:pt-[40px] lg:pt-[80px] pb-8 md:pb-[42px] gap-4">
                {/* Search Bar */}
                <form onSubmit={handleSearchSubmit} className="w-full ">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search Products"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchSubmit(e);
                                }
                            }}
                            className="w-full pl-10 pr-4 py-2 rounded-full bg-[#F5F5F5] focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder:text-[16px]"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </form>                
            </div>

            {/* Breadcrumbs */}
            {renderBreadcrumbs()}
            
            {/* Header Section with Title */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mt-4 mb-6">
                {/* Left side - Title and Description */}
                <div className="flex-1 w-full max-w-[743px] h-[165px] flex flex-col gap-[12px]" style={{ opacity: 1 }}>
                    <h1 className="text-[28px] md:text-[40px] font-bold text-gray-800 font-[LTWave]">
                        {category ? staticCategoryDisplayName : 'All Products'}
                    </h1>
                    <p className="text-[#1C1C1C] text-[19px] inter-font opacity-50">
                        Protect your crops from pests with our effective pesticide solutions. Choose from a range of products tailored for different pest types and application methods.
                    </p>
                </div>

                {/* Sort and Filter Dropdowns */}
                <div className="flex gap-4 w-full md:w-auto justify-start mt-[12px] md:mt-0 md:justify-end">
                    <FilterDropdown
                        className="bg-[#F5F5F5]"
                        label="Sort"
                        value={sortOption}
                        onChange={handleSortChange}
                        options={[
                            { value: 'newest', label: 'Newest First' },
                            { value: 'oldest', label: 'Oldest First' },
                            { value: 'name_asc', label: 'Name A-Z' },
                            { value: 'name_desc', label: 'Name Z-A' },
                        ]}
                    />
                    {/* Filter Dropdown - Commented out as requested */}
                    <FilterDropdown
                        className="bg-[#F5F5F5]"
                        label="Filter"
                        value={filterType}
                        onChange={handleFilterChange}
                        options={[
                            { value: '', label: 'All Categories' },
                            ...categories.map(cat => ({
                                value: cat.category_name || cat.name || cat.title,
                                label: cat.category_name || cat.name || cat.title
                            }))
                        ]}
                    />

                    {/* Type Dropdown */}
                    <FilterDropdown
                        className="bg-[#F5F5F5]"
                        label="Type"
                        value=""
                        onChange={() => { }}
                        options={[
                            { value: '', label: 'All' }
                        ]}
                    />
                </div>
            </div>

            {/* Products Grid or Loading/Error States */}
            {error ? (
                <div className="text-center py-12">
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            ) : loading ? (
                <div className="md:max-w-7xl mx-auto">
                    <div className="md:hidden overflow-x-auto pb-4 scrollbar-hide">
                        <div className="grid grid-cols-5 gap-4 w-max min-w-full">
                            {Array.from({ length: 15 }).map((_, index) => (
                                <ProductSkeleton key={`mobile-skeleton-${index}`} />
                            ))}
                        </div>
                    </div>
                    <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {Array.from({ length: 15 }).map((_, index) => (
                            <ProductSkeleton key={`desktop-skeleton-${index}`} />
                        ))}
                    </div>
                </div>
            ) : products.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-600 text-lg">No products found matching your criteria.</p>
                </div>
            ) : (
                <div className="md:max-w-7xl mx-auto pb-[80px]">
                    <div className="md:hidden overflow-x-auto  scrollbar-hide">
                        <div className="grid grid-cols-5 gap-4 w-max min-w-full">
                            {(() => {
                                // Create array with products and empty placeholders to maintain 5-column grid
                                const totalSlots = Math.ceil(products.length / 5) * 5; // Round up to nearest multiple of 5
                                const gridItems = [];
                                
                                for (let i = 0; i < totalSlots; i++) {
                                    if (i < products.length) {
                                        // Render actual product
                                        gridItems.push(
                                            <ProductCard 
                                                key={`mobile-${products[i]._id || products[i].id || i}`} 
                                                product={products[i]} 
                                            />
                                        );
                                    } else {
                                        // Render empty placeholder to maintain grid structure
                                        gridItems.push(
                                            <div 
                                                key={`empty-${i}`} 
                                                className="w-[244px] h-[318px] md:w-auto md:h-auto"
                                            />
                                        );
                                    }
                                }
                                
                                return gridItems;
                            })()}
                        </div>
                    </div>
                    <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {products.length > 0 ? (
                            products.map((product, index) => {
                                return (
                                    <ProductCard key={product._id || product.id || index} product={product} />
                                );
                            })
                        ) : (
                            <div className="col-span-full text-center py-8">
                                <p className="text-gray-500">No products to display</p>
                            </div>
                        )}
                    </div>                                   
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                 <div className="flex justify-center items-center mb-[176px] gap-4 flex-wrap">
                 {renderPagination}
             </div>
            )}
        </div>
    );
};

export default ProductCollection;
